/**
 * Auth0 Configuration for Cloud Landing Platform Frontend
 *
 * Provides the Auth0 SDK provider options, the localStorage token utilities
 * used by mock mode, and the shared session-expiry error matcher.
 *
 * Mock mode is a DEV-ONLY convenience: it activates when the build is a dev
 * build AND (VITE_AUTH0_MOCK_MODE === 'true' or the Auth0 env vars are
 * absent). A production build with missing Auth0 configuration fails loudly
 * at boot instead of silently degrading to mock auth.
 */

// User profile interface extending Auth0 user
export interface UserProfile {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  nickname?: string;
  picture?: string;
  updated_at?: string;
  // Custom claims from Auth0
  roles?: string[];
  permissions?: string[];
  user_metadata?: Record<string, any>;
  app_metadata?: Record<string, any>;
  organization_id?: string;
}

// Session expiry error codes from Auth0. Shared by the API SDK and the auth
// provider so the list can't drift between them again.
// Note: use spaces not underscores — Auth0 returns "Missing Refresh Token",
// not "missing_refresh_token".
export const SESSION_EXPIRY_ERRORS = [
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing refresh token',
] as const;

/**
 * True when the error indicates the Auth0 session is genuinely over (the user
 * must log in again), as opposed to a transient failure (network, rate limit)
 * that must NOT trigger a logout.
 */
export function isSessionExpiryError(error: unknown): boolean {
  const e = error as { message?: unknown; error?: unknown } | null;
  const message = [
    typeof e?.message === 'string' ? e.message : '',
    typeof e?.error === 'string' ? e.error : '',
  ]
    .join(' ')
    .toLowerCase();
  return SESSION_EXPIRY_ERRORS.some((code) => message.includes(code));
}

/**
 * Auth0 SDK provider options.
 *
 * Returns null when mock mode is active (dev only). Throws when a
 * non-dev build is missing required Auth0 configuration — a misconfigured
 * deploy must fail visibly, not ship mock auth.
 */
export const getAuth0ProviderOptions = () => {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_API_AUDIENCE;

  if (mockMode.isEnabled()) {
    console.log('Auth0: Using mock mode (dev build)');
    return null;
  }

  if (!domain || !clientId) {
    throw new Error(
      'Missing required Auth0 configuration: set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID. ' +
        'For local development without Auth0, run a dev build with VITE_AUTH0_MOCK_MODE=true.',
    );
  }

  return {
    domain,
    clientId,
    authorizationParams: {
      audience: audience || '',
      redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin,
      scope: 'openid profile email read:current_user update:current_user_metadata'
    },
    // Cache location for tokens
    cacheLocation: 'localstorage' as const,

    // Use refresh tokens
    useRefreshTokens: true,

    // Use refresh token fallback
    useRefreshTokensFallback: false,

    // Session check interval (in ms)
    sessionCheckExpiryDays: 1
  };
};

// Token storage utilities. In real-auth mode the Auth0 SDK manages its own
// token cache — these keys are only read by mock mode, so only mock mode
// should write them.
export const tokenStorage = {
  // Store access token
  setAccessToken: (token: string) => {
    localStorage.setItem('auth0_access_token', token);
  },

  // Get access token
  getAccessToken: (): string | null => {
    return localStorage.getItem('auth0_access_token');
  },

  // Remove access token
  removeAccessToken: () => {
    localStorage.removeItem('auth0_access_token');
  },

  // Store user info
  setUser: (user: UserProfile) => {
    localStorage.setItem('auth0_user', JSON.stringify(user));
  },

  // Get user info
  getUser: (): UserProfile | null => {
    const userStr = localStorage.getItem('auth0_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Remove user info
  removeUser: () => {
    localStorage.removeItem('auth0_user');
  },

  // Clear all auth data
  clear: () => {
    localStorage.removeItem('auth0_access_token');
    localStorage.removeItem('auth0_user');
    localStorage.removeItem('auth0_is_authenticated');
  }
};

const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
};

// Mock mode utilities (dev builds only — see isEnabled)
export const mockMode = {
  // Mock auth is never allowed in a production bundle. In dev it activates
  // explicitly (VITE_AUTH0_MOCK_MODE=true) or implicitly when the Auth0 env
  // vars are absent.
  isEnabled: (): boolean => {
    if (!import.meta.env.DEV) return false;
    if (import.meta.env.VITE_AUTH0_MOCK_MODE === 'true') return true;
    return !import.meta.env.VITE_AUTH0_DOMAIN || !import.meta.env.VITE_AUTH0_CLIENT_ID;
  },

  // Mock users available for development
  getMockUsers: () => {
    return [
      { key: 'owner', name: 'Organization Owner', email: 'owner@example.com', roles: ['Owner'] },
      { key: 'editor', name: 'Content Editor', email: 'editor@example.com', roles: ['Editor'] },
      { key: 'viewer', name: 'Content Viewer', email: 'viewer@example.com', roles: ['Viewer'] },
      { key: 'admin', name: 'Platform Administrator', email: 'admin@example.com', roles: ['Owner', 'Admin'] },
      { key: 'unverified', name: 'Unverified User', email: 'unverified@example.com', roles: [] }
    ];
  },

  // Switch mock user (for development)
  switchUser: async (userKey: string): Promise<UserProfile> => {
    const response = await fetch(`${getApiBaseUrl()}/auth/mock/switch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-mock-user': 'owner'
      },
      body: JSON.stringify({ userKey })
    });

    if (!response.ok) {
      throw new Error(`Failed to switch user: ${response.statusText}`);
    }

    const data = await response.json();

    // Store the new token and user data
    tokenStorage.setAccessToken(data.access_token);
    tokenStorage.setUser(data.user);

    return data.user;
  },

  // Mock login
  login: async (userKey: string = 'owner'): Promise<UserProfile> => {
    const response = await fetch(`${getApiBaseUrl()}/auth/mock/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userKey })
    });

    if (!response.ok) {
      throw new Error(`Mock login failed: ${response.statusText}`);
    }

    const data = await response.json();

    // Store the token and user data
    tokenStorage.setAccessToken(data.access_token);
    tokenStorage.setUser(data.user);
    localStorage.setItem('auth0_is_authenticated', 'true');

    return data.user;
  },

  // Mock logout
  logout: () => {
    tokenStorage.clear();
    localStorage.setItem('auth0_is_authenticated', 'false');
  }
};
